import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from '~/components/Button'
import { Checkbox } from '~/components/Checkbox'
import { Drawer } from '~/components/Drawer'
import { PreferredStoreListItem } from '~/components/PreferredStoreListItem'
import StoreSearchForm from '~/components/StoreSearchForm/StoreSearchForm'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  usePage,
  useSession,
  useStoresList,
  useUserLocation,
  useUserPreferredStore,
} from '~/hooks'
import { Coordinates } from '~/types'
import { GTM, GTMEvent } from '~/lib'
import { LoadingSpinner } from '../LoadingSpinner'
import styles from './PreferredStoreConfigureDrawer.module.css'
import { PreferredStoreConfigureDrawerProps } from './PreferredStoreConfigureDrawer.types'

export const PreferredStoreConfigureDrawer = ({
  open,
  onClose,
  onSelectStore,
  sku,
}: PreferredStoreConfigureDrawerProps) => {
  const {
    store: mySelectedStore,
    isFetchingStore,
    canClickAndCollect,
    setCanClickAndCollect,
  } = useUserPreferredStore()
  const { stores, isLoading, fetchStoresByLocation, clearStores } =
    useStoresList()
  const { session } = useSession()
  const { lastGeolocation } = useUserLocation()
  const page = usePage()
  const isRTM = page.brand === 'rtm'
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const storesLimit = 5
  const [isRunSearch, setIsRunSearch] = useState(false)

  const selectedStore = stores?.find((s) => {
    return s.storeKey === selectedKey
  })

  const isSelectable =
    selectedStore && selectedStore.storeKey !== mySelectedStore?.storeKey

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleSearch = useCallback(
    async (location: Coordinates) => {
      await fetchStoresByLocation(
        location,
        session,
        storesLimit,
        sku,
        sku && canClickAndCollect
      )
      setIsRunSearch(true)
    },
    [fetchStoresByLocation, session, sku, canClickAndCollect]
  )

  const handleSelectStore = useCallback(() => {
    if (!isSelectable || !onSelectStore) {
      return
    }
    const {
      storeKey,
      address: { postalCode },
      isInStoreAvailable,
      displayName,
      supplyChannelId,
    } = selectedStore
    onSelectStore(storeKey, postalCode, displayName, supplyChannelId)
    GTM.dispatch(
      GTMEvent.CHECK_STORE_AVAILABILITY,
      isInStoreAvailable ? 'Y' : 'N'
    )
  }, [isSelectable, selectedStore, onSelectStore])

  useEffect(() => {
    if (!open) {
      return
    }
    if (sku || session.token.storeKey) {
      setIsRunSearch(false)
      clearStores()
    }
  }, [clearStores, open, session?.token?.storeKey, sku])

  useEffect(() => {
    if (!open || session.token.storeKey || mySelectedStore) {
      return
    }

    lastGeolocation && handleSearch(lastGeolocation)
  }, [
    handleSearch,
    lastGeolocation,
    mySelectedStore,
    open,
    session?.token?.storeKey,
  ])

  useEffect(() => {
    const latitude = mySelectedStore?.latitude ?? null
    const longitude = mySelectedStore?.longitude ?? null
    const hasCoordinates = latitude != null && longitude != null

    if (!open || !hasCoordinates) {
      return
    }

    const location: Coordinates = {
      latitude: Number(latitude),
      longitude: Number(longitude),
    }

    handleSearch(location)
  }, [open, mySelectedStore, sku, handleSearch])

  const drawerTitle = (
    <div className='text-primary-heading pr-5'>
      <Typography
        tag={TypographyTag.p}
        variant={TypographyVariant.BodyLargeBold}>
        Visit us at your preferred Jaycar Store
      </Typography>
    </div>
  )

  const renderContent = () => {
    return (
      <>
        <div className=' w-full p-6 pb-4'>
          <StoreSearchForm
            label={
              <Typography
                tag={TypographyTag.span}
                variant={TypographyVariant.BodyRegular}
                className='block pb-2 text-secondary-muted'>
                Shop in store or online, and collect your order from your
                preferred Jaycar store.
              </Typography>
            }
            placeholder='Enter a suburb or postcode'
            showSearchIcon
            onLocate={handleSearch}
          />
          {sku && (
            <div className='py-4'>
              <Checkbox
                label='Only show stores with stock'
                name='clickAndCollectToggle'
                size='md'
                checked={canClickAndCollect}
                onChange={(value) => {
                  setCanClickAndCollect(value)
                }}
                disabled={isLoading || isFetchingStore}
              />
            </div>
          )}
        </div>
        <div
          className={classNames(
            'flex-1 relative px-6',
            isLoading || isFetchingStore ? 'overflow-hidden' : 'overflow-y-auto'
          )}>
          {!stores?.length ? (
            <div>
              {!isRunSearch && mySelectedStore?.displayName ? (
                <ul>
                  <li className='py-6 border-b border-primary-border'>
                    <PreferredStoreListItem
                      store={mySelectedStore}
                      isSelected
                      sku={sku}
                    />
                  </li>
                </ul>
              ) : (
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyRegular}
                  className='text-center pt-8'>
                  {isRunSearch ? 'No stores found matching your search' : ''}
                </Typography>
              )}
            </div>
          ) : (
            <ul>
              {stores?.map((store, index) => {
                return (
                  <li
                    key={index}
                    className='py-6 border-b last-of-type:border-0 border-primary-border'>
                    <PreferredStoreListItem
                      store={store}
                      isSelected={
                        selectedStore
                          ? store.storeKey === selectedStore?.storeKey
                          : store.storeKey === mySelectedStore?.storeKey
                      }
                      sku={sku}
                      onSelect={() => {
                        return setSelectedKey(store.storeKey)
                      }}
                    />
                  </li>
                )
              })}
            </ul>
          )}
          <div
            className={classNames(
              'absolute top-0 left-0 right-0 h-full hidden',
              styles.dropFilter,
              { '!flex': isLoading || isFetchingStore }
            )}>
            <LoadingSpinner />
          </div>
        </div>
        <div
          className={classNames(
            styles.footer,
            'w-full p-6 border-t border-primary-border space-y-4'
          )}>
          <Button
            style={{ '--btn-size-md-height': '48px' }}
            fluid
            disabled={!isSelectable}
            onClick={handleSelectStore}>
            Select this store
          </Button>
          {isRTM && (
            <Button
              style={{ '--btn-size-md-height': '48px' }}
              status='secondary'
              fluid
              href='/store-finder'
              onClick={handleClose}>
              View Store Finder
            </Button>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <Drawer
        wrapperClass={classNames(styles.wrapper, 'hidden md:block')}
        title={drawerTitle}
        isOpen={open}
        placement='right'
        onClose={handleClose}>
        {renderContent()}
      </Drawer>
      <Drawer
        wrapperClass={classNames(styles.wrapperBottom, 'md:hidden')}
        title={drawerTitle}
        isOpen={open}
        placement='bottom'
        onClose={handleClose}>
        {renderContent()}
      </Drawer>
    </>
  )
}
